// useRolloverPicks — reads the latest published rollover slips for all tiers.
// Returns the picks grouped by tier for the daily card display.

import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { calendarDateInAccra } from "../predictions/sessionDate.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface RolloverPick {
  tier: string;
  match: string;
  league: string;
  league_short: string | null;
  country_code: string | null;
  kickoff_at: string | null;
  market: string;
  pick: string;
  odds: number;
  confidence: number;
  fixture_id: string;
  reasoning: string | null;
}

export interface RolloverPicksState {
  picks: RolloverPick[];
  loading: boolean;
  error: string | null;
}

export function useRolloverPicks(): RolloverPicksState {
  const { user } = useAuthSession();
  const [picks, setPicks] = useState<RolloverPick[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!user) return;
    const timer = window.setInterval(() => setRefreshTick((tick) => tick + 1), 60_000);
    return () => window.clearInterval(timer);
  }, [user]);

  useEffect(() => {
    if (!user) {
      hasLoaded.current = false;
      setPicks([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    if (!hasLoaded.current) setLoading(true);

    void (async () => {
      try {
        const sessionDate = calendarDateInAccra(new Date());
        if (!sessionDate) throw new Error("Could not resolve the Accra session date");

        // A new Accra day starts empty until all four tiers are rebuilt.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const q = (client as any)
          .from("rollover_slips")
          .select("tier, selections, combined_odds, published_at, session_date")
          .eq("status", "published")
          .eq("session_date", sessionDate)
          .not("published_at", "is", null)
          .order("published_at", { ascending: false })
          .limit(20);
        const resp = await q;
        const data = resp.data as any[] | null;
        const err = resp.error as { message: string } | null;

        if (cancelled) return;
        if (err) {
          setError(err.message);
          setLoading(false);
          hasLoaded.current = true;
          return;
        }

        // Dedup: keep only the latest per tier
        const seenTiers = new Set<string>();
        const allPicks: RolloverPick[] = [];

        for (const row of (data ?? []) as any[]) {
          const tier = String(row.tier ?? "");
          if (seenTiers.has(tier)) continue;
          seenTiers.add(tier);

          const selections = Array.isArray(row.selections) ? row.selections : [];
          for (const sel of selections) {
            allPicks.push({
              tier,
              match: String(sel.match ?? ""),
              league: String(sel.league ?? ""),
              league_short: typeof sel.league_short === "string" ? sel.league_short : null,
              country_code: typeof sel.country_code === "string" ? sel.country_code : null,
              kickoff_at: typeof sel.kickoff_at === "string" ? sel.kickoff_at : null,
              market: String(sel.market ?? ""),
              pick: String(sel.pick ?? ""),
              odds: Number(sel.odds ?? 0),
              confidence: Number(sel.confidence ?? 0),
              fixture_id: String(sel.fixture_id ?? ""),
              reasoning: sel.reasoning ?? null,
            });
          }
        }

        setPicks(allPicks);
        setLoading(false);
        hasLoaded.current = true;
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
        hasLoaded.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshTick, user]);

  return useMemo(() => ({ picks, loading, error }), [picks, loading, error]);
}
